import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, FormControlLabel, LinearProgress, MenuItem, Select, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs } from '@mui/material'
import { H3 } from '../../layouts/TextStyles'
import { ui, tone } from '../../utils/uiTokens'
import { capitalize } from '../../utils/functions'
import { filterField, filterLabel } from '../../components/filterSelect/FilterSelect'
import { reconcileApi, RcCount, RcIssue, RcMentorRow, RcSeverity } from '../../features/reconcile/reconcileApi'
import { useDirectorSubject, useHeadScope } from '../head/headScope'
import { bodyRow, headRow, kpiRow, page, sub, tableWrap } from '../director/ui'

// «Оқушылар саны»: CRM против платформы и число учеников ментора по экранам.
// Модератор и админ - на /student-count, басшы и сервис басшы - в своих кабинетах.

const SEVERITY_TONE: Record<RcSeverity, typeof tone.bad> = { ERROR: tone.bad, WARN: tone.warn, INFO: tone.info }

const StudentReconcilePage = () => {
    const { formatMessage, locale } = useIntl()
    const { director } = useHeadScope()
    const directorSubject = useDirectorSubject()
    const [tab, setTab] = useState<'crm' | 'mentors'>('crm')
    const [courseId, setCourseId] = useState(0)
    // у сервис басшы стартуем с «Пән» из шапки кабинета; subjectId уходит в запрос явно и становится его областью
    const [subjectId, setSubjectId] = useState(director ? directorSubject.subjectId ?? 0 : 0)

    const t = (r: object, field: string) => r[`${field}${capitalize(locale)}`] || r[`${field}Ru`] || ''
    const { data: options = [] } = reconcileApi.useGetReconcileOptionsQuery()
    const courses = useMemo(() => {
        const m = new Map<number, string>()
        options.forEach((o) => m.set(o.courseId, t(o, 'courseTitle')))
        return Array.from(m.entries()).sort((a, b) => b[0] - a[0])
    }, [options, locale])
    const subjects = useMemo(() => {
        const m = new Map<number, string>()
        options.filter((o) => !courseId || o.courseId === courseId).forEach((o) => m.set(o.subjectId, t(o, 'subjectTitle')))
        return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]))
    }, [options, courseId, locale])

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <H3>{formatMessage({ id: 'rcTitle' })}</H3>
                <Box sx={{ ...sub, margin: '6px 0 14px' }}>{formatMessage({ id: 'rcSub' })}</Box>

                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    sx={{ marginBottom: '14px', '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, color: ui.sub }, '& .Mui-selected': { color: `${ui.pink} !important` }, '& .MuiTabs-indicator': { background: ui.pink } }}
                >
                    <Tab value="crm" label={formatMessage({ id: 'rcTabCrm' })} />
                    <Tab value="mentors" label={formatMessage({ id: 'rcTabMentors' })} />
                </Tabs>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: '16px' }}>
                    <Box>
                        <Box sx={filterLabel}>{formatMessage({ id: 'rcCourse' })}</Box>
                        <Select size="small" displayEmpty value={courseId || ''} onChange={(e) => setCourseId(Number(e.target.value) || 0)} sx={{ ...filterField, width: 'auto', minWidth: 220, maxWidth: '100%' }}>
                            <MenuItem value="">{formatMessage({ id: 'rcAllCourses' })}</MenuItem>
                            {courses.map(([id, title]) => (
                                <MenuItem key={id} value={id}>{title}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box>
                        <Box sx={filterLabel}>{formatMessage({ id: 'colSubject' })}</Box>
                        <Select size="small" displayEmpty value={subjectId || ''} onChange={(e) => setSubjectId(Number(e.target.value) || 0)} sx={{ ...filterField, width: 'auto', minWidth: 200, maxWidth: '100%' }}>
                            <MenuItem value="">{formatMessage({ id: 'salaryAllSubjects' })}</MenuItem>
                            {subjects.map(([id, title]) => (
                                <MenuItem key={id} value={id}>{title}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                {tab === 'crm' ? <CrmTab courseId={courseId} subjectId={subjectId} t={t} /> : <MentorsTab courseId={courseId} subjectId={subjectId} />}
            </Box>
        </Box>
    )
}

const Card: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode; colors?: typeof tone.bad; active?: boolean; onClick?: () => void }> = ({
    label,
    value,
    hint,
    colors,
    active,
    onClick,
}) => (
    <Box
        onClick={onClick}
        sx={{
            borderRadius: '16px',
            padding: '14px 16px',
            cursor: onClick ? 'pointer' : 'default',
            background: colors ? colors.bg : ui.card,
            border: `${active ? 2 : 1}px solid ${active ? ui.pink : colors ? colors.border : ui.border}`,
        }}
    >
        <Box sx={{ fontSize: '12.5px', color: colors ? colors.text : ui.sub }}>{label}</Box>
        <Box sx={{ fontSize: '24px', fontWeight: 800, color: colors ? colors.text : ui.ink, marginTop: '4px' }}>{value}</Box>
        {hint && <Box sx={{ fontSize: '12px', marginTop: '2px', color: colors ? colors.text : ui.sub }}>{hint}</Box>}
    </Box>
)

const CrmTab: React.FC<{ courseId: number; subjectId: number; t: (r: object, f: string) => string }> = ({ courseId, subjectId, t }) => {
    const { formatMessage } = useIntl()
    const { data, isFetching } = reconcileApi.useGetCrmReconcileQuery({ courseId, subjectId })
    const [category, setCategory] = useState<string | null>(null)
    const counts: RcCount[] = data?.counts ?? []
    const rows = (data?.issues ?? []).filter((i) => (category ? i.category === category : i.severity !== 'INFO'))
    const current = counts.find((c) => c.category === category)
    const crmStatus = (s: string | null) =>
        (s || '')
            .split(',')
            .filter(Boolean)
            .map((x) => formatMessage({ id: `rcCrm_${x}`, defaultMessage: x }))
            .join(', ')

    return (
        <>
            {isFetching && <LinearProgress sx={{ marginBottom: 2 }} />}
            <Box sx={kpiRow}>
                <Card label={formatMessage({ id: 'rcPlatformStudents' })} value={data?.platformStudents ?? 0} />
                <Card label={formatMessage({ id: 'rcCrmApproved' })} value={data?.crmApproved ?? 0} />
                {counts.map((c) => (
                    <Card
                        key={c.category}
                        label={formatMessage({ id: `rcCat_${c.category}` })}
                        value={c.rows}
                        hint={`${formatMessage({ id: `rcSeverity_${c.severity}` })} · ${formatMessage({ id: 'rcStudentsN' }, { n: c.students })}`}
                        colors={c.rows ? SEVERITY_TONE[c.severity] : undefined}
                        active={category === c.category}
                        onClick={() => setCategory(category === c.category ? null : c.category)}
                    />
                ))}
            </Box>
            <Box sx={{ ...sub, marginBottom: '10px' }}>
                {category ? formatMessage({ id: `rcCat_${category}` }) : formatMessage({ id: 'rcErrorsOnly' })}
                {current?.severity === 'INFO' && ` · ${formatMessage({ id: 'rcInfoHint' })}`}
                {current?.truncated && ` · ${formatMessage({ id: 'rcTruncated' }, { n: rows.length })}`}
            </Box>
            <Box sx={tableWrap}>
                <Table sx={{ minWidth: 820 }}>
                    <TableHead sx={headRow}>
                        <TableRow>
                            <TableCell>{formatMessage({ id: 'rcColStudent' })}</TableCell>
                            {data?.phoneVisible && <TableCell>{formatMessage({ id: 'phone' })}</TableCell>}
                            <TableCell>{formatMessage({ id: 'rcCourse' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'colSubject' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'rcColGroup' })}</TableCell>
                            <TableCell>{formatMessage({ id: 'rcColProblem' })}</TableCell>
                            <TableCell>CRM</TableCell>
                            <TableCell>{formatMessage({ id: 'rcColDate' })}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={bodyRow}>
                        {rows.map((i: RcIssue, n) => (
                            <TableRow key={`${i.category}-${i.userSubjectEnrollId ?? i.intakeId}-${n}`}>
                                <TableCell>{i.name || '-'}</TableCell>
                                {data?.phoneVisible && <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.phone || '-'}</TableCell>}
                                <TableCell>{t(i, 'courseTitle') || '-'}</TableCell>
                                <TableCell>{t(i, 'subjectTitle') || '-'}</TableCell>
                                <TableCell>{i.groupTitle || '-'}</TableCell>
                                <TableCell>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-block',
                                            padding: '2px 9px',
                                            borderRadius: '100px',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            background: SEVERITY_TONE[i.severity].bg,
                                            color: SEVERITY_TONE[i.severity].text,
                                            border: `1px solid ${SEVERITY_TONE[i.severity].border}`,
                                        }}
                                    >
                                        {formatMessage({ id: `rcCat_${i.category}` })}
                                    </Box>
                                </TableCell>
                                <TableCell>{crmStatus(i.crmStatus) || '-'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.date || '-'}</TableCell>
                            </TableRow>
                        ))}
                        {!isFetching && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} sx={{ textAlign: 'center', color: `${ui.sub} !important` }}>
                                    {formatMessage({ id: 'rcNoIssues' })}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </>
    )
}

const MentorsTab: React.FC<{ courseId: number; subjectId: number }> = ({ courseId, subjectId }) => {
    const { formatMessage } = useIntl()
    const ready = !!courseId && !!subjectId
    const { data, isFetching } = reconcileApi.useGetMentorReconcileQuery({ courseId, subjectId }, { skip: !ready })
    const [onlyDiff, setOnlyDiff] = useState(false)

    if (!ready) {
        return <Box sx={{ ...sub, padding: '24px 0', textAlign: 'center' }}>{formatMessage({ id: 'rcPickCourseSubject' })}</Box>
    }
    const mentors: RcMentorRow[] = (data?.mentors ?? []).filter((m) => !onlyDiff || m.differs)
    // выделяем числа, которые расходятся с большинством в строке
    const cells = (values: number[]) => {
        const freq = new Map<number, number>()
        values.forEach((v) => freq.set(v, (freq.get(v) || 0) + 1))
        const mode = Array.from(freq.entries()).sort((a, b) => b[1] - a[1])[0]?.[0]
        return values.map((v, k) => (
            <TableCell key={k} align="right" sx={v !== mode ? { fontWeight: 800, color: `${tone.bad.text} !important` } : undefined}>
                {v}
            </TableCell>
        ))
    }

    return (
        <>
            {isFetching && <LinearProgress sx={{ marginBottom: 2 }} />}
            <Box sx={{ ...sub, marginBottom: '12px' }}>{formatMessage({ id: 'rcMentorsSub' })}</Box>
            <Box sx={kpiRow}>
                <Card label={formatMessage({ id: 'rcMentorsTotal' })} value={data?.mentors.length ?? 0} />
                <Card label={formatMessage({ id: 'rcDiffMentors' })} value={data?.differCount ?? 0} colors={data?.differCount ? tone.bad : undefined} />
            </Box>
            <FormControlLabel
                control={<Switch checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} sx={{ '& .Mui-checked': { color: `${ui.pink} !important` }, '& .Mui-checked + .MuiSwitch-track': { background: `${ui.pink} !important` } }} />}
                label={formatMessage({ id: 'rcOnlyDiff' })}
                sx={{ marginBottom: '10px', color: ui.ink }}
            />
            <Box sx={tableWrap}>
                <Table sx={{ minWidth: 820 }}>
                    <TableHead sx={headRow}>
                        <TableRow>
                            <TableCell>{formatMessage({ id: 'colMentor' })} / {formatMessage({ id: 'rcColGroup' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'rcCurator' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'rcRating' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'rcSalary' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'rcSalaryList' })}</TableCell>
                            <TableCell align="right">{formatMessage({ id: 'rcDirector' })}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={bodyRow}>
                        {mentors.map((m) => {
                            return (
                                <React.Fragment key={m.mentorId}>
                                    <TableRow sx={{ background: m.differs ? tone.bad.bg : ui.surface }}>
                                        <TableCell sx={{ fontWeight: 700 }}>{m.mentorName}</TableCell>
                                        {cells([m.curatorList, m.rating, m.salary, m.salaryList, m.director])}
                                    </TableRow>
                                    {m.groups.map((g) => (
                                        <TableRow key={g.groupId}>
                                            <TableCell sx={{ paddingLeft: '28px !important', color: `${ui.sub} !important` }}>{g.groupTitle || '-'}</TableCell>
                                            {cells([g.curatorList, g.rating, g.salary])}
                                            <TableCell align="right" sx={{ color: `${ui.muted} !important` }}>-</TableCell>
                                            <TableCell align="right" sx={{ color: `${ui.muted} !important` }}>-</TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            )
                        })}
                        {!isFetching && mentors.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ textAlign: 'center', color: `${ui.sub} !important` }}>
                                    {formatMessage({ id: 'rcNoIssues' })}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </>
    )
}

export default StudentReconcilePage
